import React, { useState, useMemo } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import GenericTableFilter from '../../components/common/GenericTableFilter';
import DealerStockDealerDropdown from './components/DealerStockDealerDropdown';
import useKarsanStockVehiclesReport from './hooks/useKarsanStockVehiclesReport';
import useDealerStockReport from './hooks/useDealerStockReport';
import useStockBrandModelOptions from './hooks/useStockBrandModelOptions';
import KarsanStockVehiclesReportTable from './components/KarsanStockVehiclesReportTable';
import DealerStockVehiclesTable from './components/DealerStockVehiclesTable';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Roles } from '../../utils/roleEnums';

export default function KarsanStockVehiclesReport() {
    const { th } = useLanguage();
    const { hasAnyRole } = usePermissions();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [selectedDealer, setSelectedDealer] = useState(null);

    // Bölge Yöneticisi, Satış Müdürü, Karsan Lojistik, Admin — Karsan sekmesi + bayi filtresi
    const canViewKarsanStock = hasAnyRole([Roles.ADMIN, Roles.KARSAN_LOGISTICS, Roles.VEHICLE_SALES_MANAGER, Roles.REGIONAL_MANAGER]);
    const showDealerFilter = canViewKarsanStock;

    const dealerId = selectedDealer?.sapDealerId || null;

    // Karsan sekmesi gizliyken index 0 = Bayi; görünürken index 0 = Karsan, 1 = Bayi
    const isKarsanTabActive = canViewKarsanStock && activeTabIndex === 0;
    const isDealerTabActive = !canViewKarsanStock || activeTabIndex === 1;

    const karsanStock = useKarsanStockVehiclesReport({ enabled: canViewKarsanStock });
    const dealerStock = useDealerStockReport({ dealerId });

    const karsanBrandModel = useStockBrandModelOptions({
        variant: 'karsan',
        enabled: canViewKarsanStock && isKarsanTabActive
    });
    const dealerBrandModel = useStockBrandModelOptions({
        variant: 'dealer',
        dealerId,
        enabled: isDealerTabActive
    });

    const activeBrandModel = isKarsanTabActive ? karsanBrandModel : dealerBrandModel;

    const karsanFilterFields = useMemo(
        () => [
            {
                field: 'brand',
                headerName: th('karsanStockVehiclesReport.columns.brand'),
                type: 'custom',
                customComponent: ({ onChange }) => (
                    <Dropdown
                        value={karsanBrandModel.selectedBrand}
                        options={karsanBrandModel.brands}
                        onChange={(e) => {
                            karsanBrandModel.setSelectedBrand(e.value);
                            karsanBrandModel.setSelectedModel(null);
                            onChange(e.value ?? null);
                        }}
                        placeholder={th('filter.dropdownPlaceholder', { field: th('karsanStockVehiclesReport.columns.brand') })}
                        showClear
                        filter
                        filterBy="label"
                        optionLabel="label"
                        optionValue="value"
                        loading={karsanBrandModel.isLoading}
                        className="w-full"
                        emptyFilterMessage={th('filter.noResultsFound')}
                    />
                )
            },
            {
                field: 'model',
                headerName: th('karsanStockVehiclesReport.columns.model'),
                type: 'custom',
                customComponent: ({ onChange }) => (
                    <Dropdown
                        key={`karsan-model-${karsanBrandModel.selectedBrand || 'all'}`}
                        value={karsanBrandModel.selectedModel}
                        options={karsanBrandModel.models}
                        onChange={(e) => {
                            karsanBrandModel.setSelectedModel(e.value);
                            onChange(e.value ?? null);
                        }}
                        placeholder={th('filter.dropdownPlaceholder', { field: th('karsanStockVehiclesReport.columns.model') })}
                        showClear
                        filter
                        filterBy="label"
                        optionLabel="label"
                        optionValue="value"
                        loading={karsanBrandModel.isLoading}
                        className="w-full"
                        emptyFilterMessage={th('filter.noResultsFound')}
                    />
                )
            },
            { field: 'maktx', headerName: th('karsanStockVehiclesReport.columns.maktx'), type: 'text' },
            { field: 'matnr', headerName: th('karsanStockVehiclesReport.columns.matnr'), type: 'text' },
            { field: 'sasiNo', headerName: th('karsanStockVehiclesReport.columns.sasiNo'), type: 'text' }
        ],
        [th, karsanBrandModel]
    );

    const dealerFilterFields = useMemo(() => {
        const fields = [
            {
                field: 'brand',
                headerName: th('karsanStockVehiclesReport.columns.brand'),
                type: 'custom',
                customComponent: ({ onChange }) => (
                    <Dropdown
                        value={dealerBrandModel.selectedBrand}
                        options={dealerBrandModel.brands}
                        onChange={(e) => {
                            dealerBrandModel.setSelectedBrand(e.value);
                            dealerBrandModel.setSelectedModel(null);
                            onChange(e.value ?? null);
                        }}
                        placeholder={th('filter.dropdownPlaceholder', { field: th('karsanStockVehiclesReport.columns.brand') })}
                        showClear
                        filter
                        filterBy="label"
                        optionLabel="label"
                        optionValue="value"
                        loading={dealerBrandModel.isLoading}
                        className="w-full"
                        emptyFilterMessage={th('filter.noResultsFound')}
                    />
                )
            },
            {
                field: 'model',
                headerName: th('karsanStockVehiclesReport.columns.model'),
                type: 'custom',
                customComponent: ({ onChange }) => (
                    <Dropdown
                        key={`dealer-model-${dealerBrandModel.selectedBrand || 'all'}-${dealerId || 'all'}`}
                        value={dealerBrandModel.selectedModel}
                        options={dealerBrandModel.models}
                        onChange={(e) => {
                            dealerBrandModel.setSelectedModel(e.value);
                            onChange(e.value ?? null);
                        }}
                        placeholder={th('filter.dropdownPlaceholder', { field: th('karsanStockVehiclesReport.columns.model') })}
                        showClear
                        filter
                        filterBy="label"
                        optionLabel="label"
                        optionValue="value"
                        loading={dealerBrandModel.isLoading}
                        className="w-full"
                        emptyFilterMessage={th('filter.noResultsFound')}
                    />
                )
            },
            { field: 'maktx', headerName: th('karsanStockVehiclesReport.columns.maktx'), type: 'text' },
            { field: 'matnr', headerName: th('karsanStockVehiclesReport.columns.matnr'), type: 'text' },
            { field: 'chassisNo', headerName: th('karsanStockVehiclesReport.columns.chassisNo'), type: 'text' }
        ];

        if (showDealerFilter) {
            fields.unshift({
                field: 'dealerIdFilter',
                headerName: th('karsanStockVehiclesReport.dealerFilter'),
                type: 'custom',
                customComponent: ({ onChange }) => (
                    <DealerStockDealerDropdown
                        value={selectedDealer}
                        onChange={(selected) => {
                            setSelectedDealer(selected);
                            onChange(selected?.sapDealerId || null);
                            dealerBrandModel.setSelectedBrand(null);
                            dealerBrandModel.setSelectedModel(null);
                        }}
                        className="w-full"
                        placeholder={th('karsanStockVehiclesReport.dealerFilterPlaceholder')}
                    />
                )
            });
        }

        return fields;
    }, [th, showDealerFilter, dealerBrandModel, dealerId, selectedDealer]);

    const activeFilterFields = isKarsanTabActive ? karsanFilterFields : dealerFilterFields;
    const activeHook = isKarsanTabActive ? karsanStock : dealerStock;

    const handleApplyFilters = (filterValues) => {
        const { dealerIdFilter, ...restFilters } = filterValues;
        activeHook.handleApplyFilters({
            ...restFilters,
            brand: activeBrandModel.selectedBrand || null,
            model: activeBrandModel.selectedModel || null
        });
    };

    const handleResetFilters = () => {
        activeHook.handleResetFilters();
        if (isDealerTabActive) {
            setSelectedDealer(null);
            dealerBrandModel.reset();
        }
        if (isKarsanTabActive) {
            karsanBrandModel.reset();
        }
    };

    const customFilterTypes = useMemo(() => {
        if (isDealerTabActive && showDealerFilter) {
            return { dealerIdFilter: 'custom' };
        }
        return {};
    }, [isDealerTabActive, showDealerFilter]);

    const handleTabChange = (e) => {
        setActiveTabIndex(e.index);
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('karsanStockVehiclesReport.title')}</h1>
            </div>

            <div className="mb-2">
                <GenericTableFilter
                    key={isKarsanTabActive ? 'karsan-filters' : 'dealer-filters'}
                    filterFields={activeFilterFields}
                    initialFilters={{}}
                    onSearch={() => {}}
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={handleResetFilters}
                    customFilterTypes={customFilterTypes}
                />
            </div>

            <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
                {canViewKarsanStock && (
                    <TabPanel
                        header={
                            <span className="tab-header">
                                {th('karsanStockVehiclesReport.tabs.karsanStock')}
                                {karsanStock.totalCount > 0 && <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-blue-100 text-blue-800">{karsanStock.totalCount}</span>}
                            </span>
                        }
                    >
                        <KarsanStockVehiclesReportTable
                            items={karsanStock.items}
                            totalCount={karsanStock.totalCount}
                            loading={karsanStock.isFetching}
                            lazyParams={karsanStock.lazyParams}
                            onPage={karsanStock.onPage}
                            sortField={karsanStock.sortField}
                            sortOrder={karsanStock.sortOrder}
                            onSortChange={karsanStock.onSort}
                        />
                    </TabPanel>
                )}
                <TabPanel
                    header={
                        <span className="tab-header">
                            {th('karsanStockVehiclesReport.tabs.dealerStock')}
                            {dealerStock.totalCount > 0 && <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-green-100 text-green-800">{dealerStock.totalCount}</span>}
                        </span>
                    }
                >
                    <DealerStockVehiclesTable
                        items={dealerStock.items}
                        totalCount={dealerStock.totalCount}
                        loading={dealerStock.isFetching}
                        lazyParams={dealerStock.lazyParams}
                        onPage={dealerStock.onPage}
                        sortField={dealerStock.sortField}
                        sortOrder={dealerStock.sortOrder}
                        onSortChange={dealerStock.onSort}
                    />
                </TabPanel>
            </TabView>
        </div>
    );
}
